// A creator's profile, opened from a campaign's applicants or its
// conversations: their photos (the profile picture first), what brands rated
// them and wrote (no brand's name, Brúnó 2026-10-12), the earlier work they
// show (links open where they point, uploaded videos play here) and their
// TikTok and Instagram accounts with the numbers we looked up. A brand that
// approved one of their videos rates them here. A panel from the right;
// Escape, the close button or the backdrop closes it.

import {useCallback, useEffect, useState} from 'react';
import type {FormEvent, ReactNode} from 'react';
import type {BrandCreatorProfile, BrandCreatorRatings, BrandCreatorReference, BrandCreatorSocial} from '../../shared/brand-api';
import type {Level, ReferencePlatform} from '../../shared/creator-api';
import {Icon} from '../creator/components/Icon';
import {PersonPhoto, personInitials} from '../shared/person/Person';
import {BrandApiError, creatorFileUrl, creatorPicture, describe, fetchCreatorProfile, rateCreator} from './api';
import {RatingBadge, StarPicker, StarRow} from './stars';
import {useBrand} from './store';
import './creator-profile.css';

const LEVELS: Record<Level, string> = {starter: 'Starter', rising: 'Rising', pro: 'Pro', elite: 'Elite'};
const PLATFORMS: Record<ReferencePlatform, string> = {tiktok: 'TikTok', instagram: 'Instagram', youtube: 'YouTube', web: 'Link'};
const NUMBER = new Intl.NumberFormat('en-US', {notation: 'compact', maximumFractionDigits: 1});

/** "tiktok.com/@alex/video/123": a link without its scheme and www. */
function shortLink(url: string): string {
  return url.replace(/^https:\/\/(?:www\.)?/, '');
}

/** Opens one creator's profile in a panel; put `panel` anywhere in the page. */
export function useCreatorProfile(): {open(accountId: string): void; panel: ReactNode} {
  const [accountId, setAccountId] = useState<string | null>(null);
  const open = useCallback((id: string) => setAccountId(id), []);
  const close = useCallback(() => setAccountId(null), []);
  return {open, panel: accountId ? <CreatorProfilePanel key={accountId} accountId={accountId} onClose={close} /> : null};
}

function CreatorProfilePanel({accountId, onClose}: {accountId: string; onClose(): void}) {
  const [creator, setCreator] = useState<BrandCreatorProfile | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    fetchCreatorProfile(accountId).then(
      (result) => { if (live) setCreator(result.creator); },
      (caught: unknown) => {
        if (!live) return;
        setError(caught instanceof BrandApiError && caught.status === 404
          ? 'This creator’s profile is not available any more.'
          : 'The profile could not be loaded. Try again shortly.');
      },
    );
    return () => { live = false; };
  }, [accountId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const file = (fileId: string, width?: 128 | 480) => creatorFileUrl(accountId, fileId, width);
  // The profile picture, else the first photo, as on the applicant card.
  const picture = creator?.photos.find((photo) => photo.is_profile) ?? creator?.photos[0];
  return (
    <div className="profile-layer">
      <div className="profile-backdrop" onClick={onClose} />
      <aside className="profile-panel" role="dialog" aria-modal="true" aria-label={creator ? `${creator.name}’s profile` : 'Creator profile'}>
        <header className="profile-head">
          <PersonPhoto className="profile-avatar" sources={creator ? creatorPicture({account_id: accountId, photo_file_id: picture?.file_id ?? null}, 480) : []}
            fallback={<span className="profile-avatar">{creator ? personInitials(creator.name) : ''}</span>} />
          <div>
            <h2>{creator?.name ?? 'Creator profile'}</h2>
            {creator ? <small>{[creator.handle ? `@${creator.handle}` : '', LEVELS[creator.level], creator.country].filter(Boolean).join(' · ')}</small> : null}
            {creator ? <RatingBadge rating={creator.rating} /> : null}
          </div>
          <button className="profile-close" type="button" onClick={onClose} aria-label="Close"><Icon name="x" size={18} /></button>
        </header>
        {error ? <p className="form-alert">{error}</p> : !creator ? <div className="profile-loading"><span className="loader" /></div> : (
          <div className="profile-body">
            <section>
              <h3>Photos</h3>
              {creator.photos.length ? (
                <div className="profile-photos">
                  {creator.photos.map((photo) => (
                    <a key={photo.id} className={photo.is_profile ? 'profile-photo main' : 'profile-photo'} href={file(photo.file_id)} target="_blank" rel="noopener">
                      <img src={file(photo.file_id, 480)} alt={photo.is_profile ? `${creator.name}’s profile picture` : `Photo of ${creator.name}`} loading="lazy" />
                      {photo.is_profile ? <span>Profile picture</span> : null}
                    </a>
                  ))}
                </div>
              ) : <p className="muted">No photos yet.</p>}
            </section>
            <Ratings accountId={accountId} creator={creator} onSaved={(ratings) => setCreator({...creator, ...ratings})} />
            <section>
              <h3>Earlier work</h3>
              {creator.references.length ? (
                <div className="profile-list">
                  {creator.references.map((reference) => <Reference key={reference.id} reference={reference} src={reference.file_id ? file(reference.file_id) : ''} />)}
                </div>
              ) : <p className="muted">No references yet.</p>}
            </section>
            <section>
              <h3>Accounts</h3>
              {creator.socials.length ? (
                <div className="profile-list">{creator.socials.map((social) => <Social key={`${social.platform}-${social.handle}`} social={social} />)}</div>
              ) : <p className="muted">No accounts on file.</p>}
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}

function Mark({platform}: {platform: ReferencePlatform | 'video'}) {
  const icon = platform === 'tiktok' ? 'tiktok' : platform === 'instagram' ? 'image' : platform === 'youtube' || platform === 'video' ? 'video' : 'link';
  return <span className={`profile-mark ${platform}`}><Icon name={icon} size={16} /></span>;
}

function Reference({reference, src}: {reference: BrandCreatorReference; src: string}) {
  if (reference.kind === 'video') {
    return (
      <div className="profile-video">
        <video src={src} controls playsInline preload="metadata" aria-label={reference.file_name || 'Uploaded video'} />
        <small><Mark platform="video" />{reference.file_name || 'Uploaded video'}</small>
      </div>
    );
  }
  const platform = reference.platform ?? 'web';
  return (
    <a className="profile-row" href={reference.url} target="_blank" rel="noopener noreferrer">
      <Mark platform={platform} />
      <span><b>{PLATFORMS[platform]}</b><small>{shortLink(reference.url)}</small></span>
      <Icon name="external" size={15} />
    </a>
  );
}

function Social({social}: {social: BrandCreatorSocial}) {
  const numbers = ([[social.followers, 'followers'], [social.likes, 'likes'], [social.videos, 'videos']] as const)
    .filter(([value]) => value !== null).map(([value, label]) => `${NUMBER.format(value!)} ${label}`);
  return (
    <a className="profile-row" href={social.url} target="_blank" rel="noopener noreferrer">
      <Mark platform={social.platform} />
      <span>
        <b>{social.platform === 'tiktok' ? 'TikTok' : 'Instagram'} · @{social.handle}{social.verified ? <em>Verified with TikTok</em> : null}</b>
        <small>{numbers.join(' · ') || 'No numbers yet'}</small>
      </span>
      <Icon name="external" size={15} />
    </a>
  );
}

const MONTH = new Intl.DateTimeFormat('en-US', {month: 'short', year: 'numeric', timeZone: 'UTC'});

/** "2026-10" as "Oct 2026". */
function monthLabel(month: string): string {
  const date = new Date(`${month}-01T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? '' : MONTH.format(date);
}

/**
 * What brands rated the creator, and this brand's own rating once it approved
 * one of their videos. Other brands read the stars and the comment, never the
 * brand's name.
 */
function Ratings({accountId, creator, onSaved}: {accountId: string; creator: BrandCreatorProfile; onSaved(ratings: BrandCreatorRatings): void}) {
  const {toast, reload} = useBrand();
  const [stars, setStars] = useState(creator.my_rating?.stars ?? 0);
  const [comment, setComment] = useState(creator.my_rating?.comment ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!stars) { setError('Choose one to five stars.'); return; }
    setBusy(true); setError('');
    try {
      const answer = await rateCreator(accountId, {stars, comment});
      onSaved(answer.ratings);
      toast(creator.my_rating ? 'Rating updated' : `Thanks, ${creator.name} is rated`);
      // The applicant cards show the rating too.
      void reload(true);
    } catch (caught) { setError(describe(caught)); }
    finally { setBusy(false); }
  }
  return <section className="profile-ratings">
    <h3>Ratings</h3>
    <div className="profile-rating-summary">
      <RatingBadge rating={creator.rating} large />
      <small>{creator.rating.count ? `From ${creator.rating.count} ${creator.rating.count === 1 ? 'brand' : 'brands'} that worked with ${creator.name}` : 'Brands rate a creator once they approve one of their videos.'}</small>
    </div>
    {creator.can_rate ? <form className="profile-rate" onSubmit={(event) => void save(event)}>
      <b>{creator.my_rating ? 'Your rating' : `How was working with ${creator.name}?`}</b>
      <StarPicker value={stars} onChange={(next) => { setStars(next); setError(''); }} disabled={busy} />
      <textarea value={comment} maxLength={280} rows={3} placeholder="A sentence other brands can learn from (optional)"
        onChange={(event) => { setComment(event.target.value); setError(''); }} />
      {error ? <p className="sign-missing" role="alert">{error}</p> : null}
      <div className="profile-rate-foot">
        <small>Other brands see your stars and comment, never your company’s name. {280 - comment.length} characters left.</small>
        <button className="btn btn-g btn-sm" type="submit" disabled={busy}>{busy ? 'Saving…' : creator.my_rating ? 'Update rating' : 'Save rating'}</button>
      </div>
    </form> : null}
    {creator.reviews.length ? <div className="profile-reviews">{creator.reviews.map((review, index) => (
      <figure className="profile-review" key={`${review.month}-${index}`}>
        <StarRow value={review.stars} />
        <blockquote>{review.comment}</blockquote>
        <figcaption>{review.own ? 'Your rating' : 'Verified brand'} · {monthLabel(review.month)}</figcaption>
      </figure>
    ))}</div> : null}
  </section>;
}
